/* tslint:disable */
/** general classification of delay (upper 8 bits of 16 bit code value) */
export enum DelayType {
  DELAY_TYPE_DISPATCH      = 0x0000,
  DELAY_TYPE_SPD_CHANGE    = 0x0100,
  DELAY_TYPE_RANDOM        = 0x0200,
  DELAY_TYPE_SCHEDULED     = 0x0300,
  DELAY_TYPE_FAILURE       = 0x0400,
  DELAY_TYPE_STOP_EVENT    = 0x0500,
  DELAY_TYPE_TRAIN_CONSIST = 0x0600,
  DELAY_TYPE_UNKNOWN       = 0x0700
}

/** specific reason for delay (lower 8 bits of 16 bit code value) */
export enum DelaySubType {
  DELAY_SUB_TYPE_NO_ROUTE          = 0x01,
  DELAY_SUB_TYPE_NO_SIGNAL         = 0x02,
  DELAY_SUB_TYPE_SWITCH_BUSY       = 0x03,
  DELAY_SUB_TYPE_QUEUED            = 0x04,
  DELAY_SUB_TYPE_PARKED            = 0x05,
  DELAY_SUB_TYPE_NO_CAPACITY       = 0x06,
  DELAY_SUB_TYPE_NO_AUTHORITY      = 0x07,
  DELAY_SUB_TYPE_SIGNAL_STOP       = 0x08,
  DELAY_SUB_TYPE_HEADWAY           = 0x09,
  DELAY_SUB_TYPE_HOLDING           = 0x0A,
  DELAY_SUB_TYPE_ACCEL_FROM_STOP   = 0x01,
  DELAY_SUB_TYPE_DECEL_MOVING      = 0x02,
  DELAY_SUB_TYPE_ACCEL_MOVING      = 0x03,
  DELAY_SUB_TYPE_DECEL_TO_STOP     = 0x04,
  DELAY_SUB_TYPE_HOLD_FOR_SCHEDULE = 0x01,
  DELAY_SUB_TYPE_STOP_CREW_CHANGE  = 0x01,
  DELAY_SUB_TYPE_STOP_POWER_CHANGE = 0x02,
  DELAY_SUB_TYPE_STOP_WORK_EVENT   = 0x03,
  DELAY_SUB_TYPE_STOP_PSG_STOP     = 0x04,
  DELAY_SUB_TYPE_STOP_OTHER        = 0x05,
  DELAY_SUB_TYPE_DIAMOND_CROSSING  = 0x0B,
  DELAY_SUB_TYPE_REACTION_TIME     = 0x05,
  DELAY_SUB_TYPE_UNKNOWN           = 0xFF
}

export type DelayTypeMask = number;

/**
 *
 * @class MovementDelay
 *
 * @brief as they move through the rail network, trains experience delays, we account for these
 * at each decision point.  The Movement arrive/depart times already include these delays, but
 * the details here allow a user to see what delays were part of the transit time leading into
 * any given decision point.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2019
 *
 */
export class MovementDelay {

  private static _colors: number[] = [
    0xd6a090,
    0xfe3b1e,
    0xa12c32,
    0xfa2f7a,
    0xfb9fda,
    0xe61cf7,
    0x992f7c,
    0x051155,
    0x4f02ec,
    0x2d69cb,
    0x00a6ee,
    0x6febff,
    0x08a29a,
    0x2a666a,
    0xb7c0ff,
    0xffffff,
    0xacbe9c,
    0x827c70,
    0x5a3b1c,
    0xae6507,
    0xf7aa30,
    0xf4ea5c,
    0x9b9500,
    0x566204,
    0x11963b,
    0x51e113,
    0x08fdcc,
    0x47011f
  ];

  /** @var _type DelayTypeMask the bit mask that encodes the kind(s) of delay for this value */
  public  _type: DelayTypeMask = 0;

  /** @var _hours  number the delay time (hours) */
  public _hours: number        = 0;

  /** @brief standard constructor */
  constructor(code: DelayTypeMask, delay: number) {

    this._type  = code;
    this._hours = delay;
  }

  public static typeToString(value: DelayTypeMask): string {

    switch (value & 0xFF00) {
      case DelayType.DELAY_TYPE_DISPATCH:                   return 'DISPATCH';
      case DelayType.DELAY_TYPE_SPD_CHANGE:                 return 'SPEED CHANGE';
      case DelayType.DELAY_TYPE_RANDOM:                     return 'RANDOM';
      case DelayType.DELAY_TYPE_SCHEDULED:                  return 'SCHEDULED';
      case DelayType.DELAY_TYPE_FAILURE:                    return 'FAILURE';
      case DelayType.DELAY_TYPE_STOP_EVENT:                 return 'STOP';
      case DelayType.DELAY_TYPE_TRAIN_CONSIST:              return 'CONSIST CHANGE';
    }

    return 'UNKONWN';
  }

  public static subTypeToString(value: DelayTypeMask): string {

    switch (value & 0xFF00) {

      case DelayType.DELAY_TYPE_DISPATCH: {

        switch (value & 0xFF) {

          case DelaySubType.DELAY_SUB_TYPE_NO_ROUTE:          return 'NO ENABLED ROUTE';
          case DelaySubType.DELAY_SUB_TYPE_NO_CAPACITY:       return 'NO CAPACITY';
          case DelaySubType.DELAY_SUB_TYPE_SWITCH_BUSY:       return 'SWITCH BUSY';
          case DelaySubType.DELAY_SUB_TYPE_QUEUED:            return 'QUEUED';
          case DelaySubType.DELAY_SUB_TYPE_PARKED:            return 'PARKED';
          case DelaySubType.DELAY_SUB_TYPE_NO_SIGNAL:         return 'NO CEAR SIGNAL';  //not used anywhere yet
          case DelaySubType.DELAY_SUB_TYPE_SIGNAL_STOP:       return 'SIGNAL STOP';
          case DelaySubType.DELAY_SUB_TYPE_NO_AUTHORITY:      return 'NO AUTHORITY';
          case DelaySubType.DELAY_SUB_TYPE_HEADWAY:           return 'HEADWAY';
          case DelaySubType.DELAY_SUB_TYPE_HOLDING:           return 'HOLDING';
          case DelaySubType.DELAY_SUB_TYPE_DIAMOND_CROSSING:   return 'DIAMOND CROSSING BLOCKED';
        }

        return 'UNDEFINED SUBTYPE';
      }

      case DelayType.DELAY_TYPE_SPD_CHANGE: {

        switch (value & 0xFF) {

          case DelaySubType.DELAY_SUB_TYPE_ACCEL_FROM_STOP:   return 'ACCEL FROM STOP';
          case DelaySubType.DELAY_SUB_TYPE_DECEL_TO_STOP:     return 'DECEL TO STOP';
          case DelaySubType.DELAY_SUB_TYPE_ACCEL_MOVING:      return 'ACCEL MOVING';
          case DelaySubType.DELAY_SUB_TYPE_DECEL_MOVING:      return 'DECEL MOVING';
          case DelaySubType.DELAY_SUB_TYPE_REACTION_TIME:      return 'REACTION TIME';
        }

        return 'UNDEFINED SUBTYPE';
      }

      case DelayType.DELAY_TYPE_RANDOM:                       return 'UNKNOWN';
      case DelayType.DELAY_TYPE_SCHEDULED:                    return 'HOLD FOR SCHEDULE';
      case DelayType.DELAY_TYPE_FAILURE:                      return 'UNKNOWN';

      case DelayType.DELAY_TYPE_STOP_EVENT: {

        switch (value & 0xFF) {

          case DelaySubType.DELAY_SUB_TYPE_STOP_CREW_CHANGE:  return 'CREW CHANGE';
          case DelaySubType.DELAY_SUB_TYPE_STOP_POWER_CHANGE: return 'POWER CHANGE';
          case DelaySubType.DELAY_SUB_TYPE_STOP_WORK_EVENT:   return 'WORK EVENT';
          case DelaySubType.DELAY_SUB_TYPE_STOP_PSG_STOP:     return 'PSG STOP';
          case DelaySubType.DELAY_SUB_TYPE_STOP_OTHER:        return 'OTHER';

        }

        return 'UNDEFINED SUBTYPE';
      }

      case DelayType.DELAY_TYPE_TRAIN_CONSIST:

        switch (value & 0xFF) {

          case DelaySubType.DELAY_SUB_TYPE_STOP_OTHER: return 'OTHER';

          default: return 'UNDEFINED SUBTYPE';
        }

    }

    return 'UNKNOWN';
  }

  /** @brief convert the combined code into just the type */
  public static toDelayType(code:DelayTypeMask):DelayType {

    return <DelayType>((code >>> 0) & 0xFF00);
  }

  /** @brief convert the combined code into just the subtype */
  public static toDelaySubType(code:DelayTypeMask):DelaySubType {

    return <DelaySubType>((code >>> 0) & 0x00FF);
  }

  /** @brief decode upper 8 bits to provide a string value for general classification */
  public static typeString(code:DelayTypeMask):string {

    return MovementDelay.typeToString(code);
  }

  /** @brief decode the lower 8 bits to provide a string value for the specific reason */
  public static subTypeString(code:DelayTypeMask):string {

    return MovementDelay.subTypeToString(code);
  }

  /** @brief fetch RGB value for this type */
  public static colorOfDelayType(t:DelayType):number {

    return MovementDelay._colors[<number>t];
  }

  /** @brief fetch RGB value for this subtype */
  public static colorOfDelaySubType(s:DelaySubType):number {

    return MovementDelay._colors[8 + <number>s];
  }
}
